import {
    Component,
    h,
    Prop,
    ComponentInterface,
    Element,
    Method,
    Event,
    EventEmitter,
    Host,
    Watch,
} from "@stencil/core";
import PopoverImpl from "../../utils/popover";

export type TriggerType = "key" | "click";
export interface PopoverShowEvent {
    triggerType?: TriggerType;
}

export type Placement =
    | "top"
    | "top-start"
    | "top-end"
    | "bottom"
    | "bottom-start"
    | "bottom-end"
    | "right"
    | "right-start"
    | "right-end"
    | "left"
    | "left-start"
    | "left-end";

/**
 * @slot - Default content is placed inside the popover
 * @slot trigger - A component that triggers opening/closing the popover
 */
@Component({
    tag: "uic-popover",
    styleUrl: "./popover.scss",
})
export class Popover implements ComponentInterface {
    trigger: HTMLElement;
    triggerType?: TriggerType;
    panel: HTMLElement;
    panelPositioner: HTMLElement;
    popover: PopoverImpl;

    @Element() host: HTMLUicPopoverElement;

    /**
     * The element to use for positioning. If the user interacts outside of the element the popover will close.
     */
    @Prop({ mutable: true }) containingElement: HTMLElement;

    /**
     * If set to `true`, the popover is visible.
     */
    @Prop({ mutable: true, reflect: true }) open = false;

    /**
     * The preferred placement of the dropdown panel.The actual placement may vary to keep the panel
     * inside of the viewport.
     */
    @Prop() placement: Placement = "bottom-start";

    /**
     * The distance in pixels from which to offset the panel away from its trigger.
     */
    @Prop() distance: number = 4;

    /**
     * The distance in pixels from which to offset the panel along its trigger.
     */
    @Prop() skidding: number = 0;

    /**
     * Set to `true` to prevent the panel from being clipped when the component is placed inside a container with
     * `overflow: auto|scroll|hidden`.
     */
    @Prop() hoist: boolean = false;

    /**
     * Set to `true` to automatically close the popover when a list item is selected.
     */
    @Prop() closeOnSelect: boolean = true;

    /**
     * Emitted when the popover opens.
     */
    @Event() uicShow: EventEmitter<PopoverShowEvent>;

    /**
     * Emitted after the popover opened.
     * Useful for handling focus.
     */
    @Event() uicAfterShow: EventEmitter<PopoverShowEvent>;

    /**
     * Emitted when the popover closes.
     */
    @Event() uicHide: EventEmitter;

    @Watch("distance")
    @Watch("skidding")
    @Watch("placement")
    @Watch("hoist")
    handleOptionsChange() {
        this.popover?.setOptions({
            placement: this.placement,
            strategy: this.hoist ? "fixed" : "absolute",
            distance: this.distance,
            skidding: this.skidding,
        });
    }

    @Watch("open")
    handleOpenChange() {
        if (this.open) {
            this.uicShow.emit({ triggerType: this.triggerType });

            document.addEventListener(
                "mousedown",
                this.handleDocumentMouseDown
            );
            document.addEventListener("keydown", this.handleDocumentKeyDown);
            this.panel.addEventListener("uicSelect", this.handleListSelect);
            this.panel.addEventListener(
                "uicClosePopover",
                this.handleClosePopover
            );
        } else {
            this.uicHide.emit();

            document.removeEventListener(
                "mousedown",
                this.handleDocumentMouseDown
            );
            document.removeEventListener("keydown", this.handleDocumentKeyDown);
            this.panel.removeEventListener("uicSelect", this.handleListSelect);
            this.panel.removeEventListener(
                "uicClosePopover",
                this.handleClosePopover
            );
        }
    }

    connectedCallback() {
        if (!this.containingElement) {
            this.containingElement = this.host;
        }

        this.handleDocumentKeyDown = this.handleDocumentKeyDown.bind(this);
        this.handleDocumentMouseDown = this.handleDocumentMouseDown.bind(this);

        this.handleListSelect = this.handleListSelect.bind(this);
        this.handleClosePopover = this.handleClosePopover.bind(this);
    }

    componentDidLoad() {
        if (this.open) {
            this.handleOpenChange();
        }
    }

    componentDidUpdate() {
        if (!this.popover) {
            this.popover = new PopoverImpl(this.trigger, this.panelPositioner, {
                placement: this.placement,
                strategy: this.hoist ? "fixed" : "absolute",
                distance: this.distance,
                skidding: this.skidding,
            });
        }

        if (this.open && this.triggerType) {
            this.uicAfterShow.emit({ triggerType: this.triggerType });
            this.triggerType = null;
        }

        requestAnimationFrame(() => this.popover.update());
    }

    disconnectedCallback() {
        this.hide();
        this.popover?.destroy();
    }

    /** show the popover */
    @Method()
    async show() {
        if (this.open) {
            return;
        }

        this.open = true;
    }

    /** hide the popover */
    @Method()
    async hide() {
        if (!this.open) {
            return;
        }

        this.open = false;
    }

    private focusOnTrigger() {
        const trigger = this.trigger.querySelector(
            "button, input, [tabindex]:not([tabindex='-1'])"
        ) as any;
        if (trigger) {
            if (typeof trigger.setFocus === "function") {
                trigger.setFocus();
            } else if (typeof trigger.focus === "function") {
                trigger.focus();
            }
        }
    }

    private handleDocumentKeyDown(event: KeyboardEvent) {
        if (event.key === "Escape") {
            this.hide();
            this.focusOnTrigger();
            return;
        }

        if (event.key === "Tab") {
            setTimeout(() => {
                if (
                    document.activeElement &&
                    document.activeElement.closest(
                        this.containingElement.tagName.toLowerCase()
                    ) !== this.containingElement
                ) {
                    this.hide();
                    return;
                }
            });
        }

        if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
            event.preventDefault();
        }

        const menu = this.getList();

        if (menu && ["ArrowDown", "ArrowUp"].includes(event.key)) {
            event.preventDefault();
            menu.setFocus();
        }
    }

    private handleDocumentMouseDown(event: MouseEvent) {
        const path = event.composedPath();
        if (!path.includes(this.containingElement)) {
            requestAnimationFrame(() => this.hide());
            return;
        }
    }

    private handleTriggerKeyDown(event: KeyboardEvent) {
        if (!this.open && ["ArrowDown", "ArrowUp"].includes(event.key)) {
            this.triggerType = "key";
            this.show();

            event.preventDefault();
            event.stopPropagation();

            requestAnimationFrame(() => this.setChildFocus("key"));
        }
    }

    private handleTriggerClick() {
        if (this.open) {
            this.hide();
        } else {
            this.triggerType = "click";
            this.show();
            requestAnimationFrame(() => this.setChildFocus("click"));
        }
    }

    private setChildFocus(trigger: TriggerType) {
        const inputSelected = this.setFocusToInput();
        if (!inputSelected && trigger === "key") {
            this.setFocusToList();
        }
    }

    private setFocusToInput(): boolean {
        const input = this.getInput();
        if (!input) {
            return false;
        }

        input.setFocus();
        return true;
    }

    private removeFocusFromInput() {
        const input = this.getInput();
        input?.removeFocus();
    }

    private handleClosePopover(event: CustomEvent) {
        event.stopPropagation();
        this.hide();
        this.removeFocusFromInput();
        this.removeFocusFromList();
        this.focusOnTrigger();
    }

    private handleListSelect(event: CustomEvent) {
        const target = event.target as HTMLElement;
        const tagName = target.tagName.toLowerCase();
        if (
            this.closeOnSelect &&
            (tagName === "uic-list" || tagName === "uic-list-item")
        ) {
            this.hide();
            this.removeFocusFromList();
        }
    }

    private removeFocusFromList() {
        const list = this.getList();
        list?.removeFocus();
    }

    private setFocusToList() {
        const list = this.getList();
        list?.setFocus();
    }

    private getList(): HTMLUicListElement | undefined {
        return Array.prototype.filter.call(
            this.panel.children,
            (child: HTMLElement) => child.tagName.toLowerCase() === "uic-list"
        )[0];
    }

    private getInput(): HTMLUicInputFieldElement | undefined {
        return this.panel.querySelector("uic-input-field");
    }

    render() {
        return (
            <Host
                class={{
                    "uic-popover": true,
                    "uic-popover--open": this.open,
                }}
            >
                <span
                    class="uic-popover__trigger"
                    onClick={() => this.handleTriggerClick()}
                    onKeyDown={(e) => this.handleTriggerKeyDown(e)}
                    ref={(el) => (this.trigger = el)}
                >
                    <slot name="trigger"></slot>
                </span>
                <div
                    ref={(el) => (this.panelPositioner = el)}
                    class="uic-popover__panel-positioner"
                >
                    <div
                        ref={(el) => (this.panel = el)}
                        class="uic-popover__panel"
                    >
                        <slot></slot>
                    </div>
                </div>
            </Host>
        );
    }
}
